// Phase H.10 — TipTap-powered WYSIWYG template editor.
//
// Replaces the previous textarea-based editor. Public prop API preserved so
// NewTemplatePage and TemplateDetailPage didn't need changes:
//   initialName, initialCategory, initialDescription, initialBodyHtml,
//   onSave({name, category, description, body_html}), saving, submitLabel.
//
// Architecture:
//   - Body content is HTML stored on the server (unchanged contract).
//   - On load, plain `{{tag.path}}` text is converted to span chips via
//     `loadHtmlForEditor` so TipTap can render them as MergeTagNode chips.
//   - On save, MergeTagNode chips are normalized back to plain
//     `{{tag.path}}` text via `saveHtmlFromEditor` so the server-side
//     `_substitute_html` PDF path keeps working unchanged.
//
// Toolbar groups are split into 9 visual sections; every interactive button
// has a `data-testid` and an `aria-label` for tests and a11y.

import React, { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
// Phase N follow-up — shared content-only sniffer for styled HTML so the
// reload-detection heuristic is unit-testable in isolation.
import { looksLikeStyledHtml } from "@/lib/htmlHeuristics";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { FontFamily } from "@tiptap/extension-font-family";
import { TextAlign } from "@tiptap/extension-text-align";
import { Link as LinkExt } from "@tiptap/extension-link";
import { Image as ImageExt } from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Placeholder } from "@tiptap/extension-placeholder";
import { CharacterCount } from "@tiptap/extension-character-count";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";

import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
    Heading1, Heading2, Heading3, Heading4, Pilcrow,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    List, ListOrdered, Quote, IndentIncrease, IndentDecrease,
    Link as LinkIcon, Image as ImageIcon, Table as TableIcon, Minus, FileText,
    Undo2, Redo2, Eraser, Search, Eye, Code2,
    AlertTriangle, CheckCircle2, Type, Palette, Highlighter,
    Subscript as SubIcon, Superscript as SupIcon, FileCode2,
    CalendarDays, Sigma, PenLine, SpellCheck2, Printer,
    BarChart3, Loader2, RefreshCw, ChevronDown,
    // Phase H.12a — section break icon (distinct from Page break `FileText`)
    // + chevron-right for collapsible + download icons for the export bar.
    SeparatorHorizontal, ChevronRight,
    FileDown, FileType2, FileCode, FileImage,
    // Phase H.12b — footnote / endnote toolbar icons.
    Asterisk, BookmarkPlus, Trash2,
    // Phase H.13a — version history icon.
    History,
    // Phase H.13b — clause library icon.
    BookOpen,
    // Phase H.13c — variable validator icon (footer toolbar toggle).
    ListChecks,
    // Phase H.13d — numbered-article toolbar toggle icon.
    Hash,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

import { MergeTagNode } from "./editor/MergeTagNode";
import { FontSize } from "./editor/FontSize";
import { SignatureLineNode } from "./editor/SignatureLineNode";
// Phase H.12a — new custom nodes (section break in body, page-number atoms
// only used inside the header/footer MiniEditor instances).
import { SectionBreakNode } from "./editor/SectionBreakNode";
// Phase H.12b — footnote / endnote inline atoms (body editor only).
import { FootnoteNode, EndnoteNode } from "./editor/FootnoteNode";
// Phase H.13d — heading class attribute + toggleNumberedSection command.
import { HeadingClass, isNumberedSectionActive } from "./editor/HeadingClass";
import MiniEditor from "./editor/MiniEditor";
import { exportTemplateAsHtml, exportTemplateAsMarkdown } from "@/lib/exportHelpers";
// Phase H.13a — version-history drawer.
import VersionHistoryDrawer from "./VersionHistoryDrawer";
// Phase H.13b — Clause library picker (body editor + footnote popover).
import ClausePickerModal from "./ClausePickerModal";
// Phase H.13c — Variable validator side panel.
import VariableValidatorPanel from "./VariableValidatorPanel";
import { loadHtmlForEditor, saveHtmlFromEditor } from "./editor/normalize";

const TAG_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;
function parseDetectedTags(html) {
    if (!html) return [];
    const found = new Set();
    let m;
    while ((m = TAG_RE.exec(html)) !== null) found.add(m[1]);
    return Array.from(found).sort();
}

const FONT_FAMILIES = [
    { label: "Default", value: "" },
    { label: "Arial", value: "Arial, sans-serif" },
    { label: "Times New Roman", value: "'Times New Roman', serif" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Courier New", value: "'Courier New', monospace" },
    { label: "Verdana", value: "Verdana, sans-serif" },
    { label: "Tahoma", value: "Tahoma, sans-serif" },
];
const FONT_SIZES = ["10pt", "11pt", "12pt", "14pt", "16pt", "18pt", "20pt", "24pt", "28pt", "36pt"];
const TEXT_COLORS = [
    { label: "Black", v: "#0f172a" }, { label: "Grey", v: "#64748b" },
    { label: "Red", v: "#dc2626" }, { label: "Orange", v: "#ea580c" },
    { label: "Green", v: "#15803d" }, { label: "Blue", v: "#1d4ed8" },
    { label: "Purple", v: "#7c3aed" },
];
const HIGHLIGHT_COLORS = [
    { label: "Yellow", v: "#fef08a" }, { label: "Green", v: "#bbf7d0" },
    { label: "Pink", v: "#fbcfe8" }, { label: "Blue", v: "#bfdbfe" },
];

// Phase H.11 — special-characters palette.
const SPECIAL_CHARS = [
    { group: "Legal / Currency", chars: ["©", "®", "™", "§", "¶", "†", "‡", "°", "¢", "£", "€", "¥"] },
    { group: "Math", chars: ["±", "×", "÷", "≈", "≠", "≤", "≥", "½", "¼", "¾", "⅓", "⅔"] },
    { group: "Punctuation", chars: ["—", "–", "…", "“", "”", "‘", "’", "«", "»", "‹", "›", "‚", "„"] },
    { group: "Arrows", chars: ["←", "→", "↑", "↓", "↔", "⇐", "⇒"] },
    { group: "Bullets / Marks", chars: ["•", "◦", "▪", "★", "☆", "✓", "✗", "⚠"] },
];

// Phase H.11 — signature-line label presets.
const SIGNATURE_LABELS = [
    "Customer signature",
    "Guarantor signature",
    "Authorized representative",
    "Date",
    "Witness",
];

const HEADING_OPTIONS = [
    { v: "p",  label: "Paragraph" },
    { v: "h1", label: "Heading 1" },
    { v: "h2", label: "Heading 2" },
    { v: "h3", label: "Heading 3" },
    { v: "h4", label: "Heading 4" },
    { v: "h5", label: "Heading 5" },
    { v: "h6", label: "Heading 6" },
];

// Phase H.11 — date format helpers (local browser time, static text).
function fmtToday(kind = "long") {
    const d = new Date();
    if (kind === "iso") {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }
    if (kind === "short") {
        return d.toLocaleDateString("en-US", {
            year: "numeric", month: "2-digit", day: "2-digit",
        });
    }
    // long
    return d.toLocaleDateString("en-US", {
        year: "numeric", month: "long", day: "numeric",
    });
}

// Phase H.11 — cheap content hash for auto-save dedupe.
function strHash(s) {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return h;
}

// Phase H.11 — detailed word-count stats from the editor's plain text + HTML.
function computeStats(editor, html, detectedTags) {
    if (!editor) return null;
    const text = editor.state.doc.textBetween(
        0, editor.state.doc.content.size, "\n", " "
    );
    const words = (text.match(/\S+/g) || []).length;
    const chars = text.length;
    const charsNoSpaces = text.replace(/\s+/g, "").length;
    const sentences = (text.match(/[.!?]+(?=\s|$)/g) || []).length;
    let paragraphs = 0;
    editor.state.doc.descendants((node) => {
        if (
            node.type.name === "paragraph" ||
            node.type.name === "heading"
        ) {
            if (node.textContent.trim().length > 0) paragraphs += 1;
        }
    });
    const readingMin = Math.max(1, Math.round(words / 200));
    return {
        words, chars, charsNoSpaces, sentences, paragraphs,
        readingMin, tags: detectedTags.length,
    };
}

// ------------------------------ Toolbar pieces ------------------------------

function TbBtn({ onClick, active, disabled, title, testid, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            aria-label={title}
            aria-pressed={active ? "true" : "false"}
            data-testid={testid}
            className={[
                "h-8 min-w-8 px-1.5 inline-flex items-center justify-center rounded-md border transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1",
                active
                    ? "bg-blue-900 text-white border-blue-900"
                    : "bg-white text-slate-700 border-transparent hover:bg-slate-100 hover:border-slate-200",
                disabled ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}>
            {children}
        </button>
    );
}

function Divider() {
    return <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />;
}

// ------------------------------ Main component ------------------------------

const TemplateEditor = forwardRef(function TemplateEditor({
    initialName = "",
    initialCategory = "",
    initialDescription = "",
    initialBodyHtml = "",
    initialHeaderHtml = "",          // Phase H.12a — running header HTML
    initialFooterHtml = "",          // Phase H.12a — running footer HTML
    templateId = null,               // Phase H.12a — required for Export PDF
    canRestoreVersions = false,      // Phase H.13a — true for super_admin only
    onSave,
    onAutoSave,           // Phase H.11 — optional silent autosaver (parent supplies for edit mode)
    onVersionRestored,    // Phase H.13a — parent reloads template after a restore
    saving = false,
    submitLabel = "Save template",
}, _ref) {
    const [name, setName] = useState(initialName);
    const [category, setCategory] = useState(initialCategory);
    const [description, setDescription] = useState(initialDescription);
    // Phase H.12a — header/footer state. Lifted here so it round-trips
    // through the same debounced auto-save as the body.
    const [headerHtml, setHeaderHtml] = useState(initialHeaderHtml || "");
    const [footerHtml, setFooterHtml] = useState(initialFooterHtml || "");
    const [headerFooterOpen, setHeaderFooterOpen] = useState(
        Boolean((initialHeaderHtml && initialHeaderHtml.trim()) ||
                (initialFooterHtml && initialFooterHtml.trim()))
    );
    const [exportingPdf, setExportingPdf] = useState(false);
    // Phase H.13a — version-history drawer state.
    const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);
    // Phase H.13c — variable validator side panel state. Independent
    // from the version drawer; both can be open at the same time.
    const [validatorOpen, setValidatorOpen] = useState(false);
    // The "current" content we compare against in the diff view is the
    // most-recent PERSISTED body_html — not the unsaved editor draft.
    // We update this whenever a save (manual or auto) actually lands.
    const [persistedBodyHtml, setPersistedBodyHtml] = useState(initialBodyHtml || "");
    // Phase H.12b — footnote / endnote popover state.
    //   noteOpen:   anchor DOM node currently being edited (null = closed)
    //   noteKind:   "footnote" | "endnote"
    //   notePos:    ProseMirror doc position of the chip (for save/delete)
    //   noteDraft:  current text in the popover input
    //   noteIsNew:  true on a freshly inserted chip (so "Cancel" deletes it)
    const [noteOpen, setNoteOpen] = useState(null);
    const [noteKind, setNoteKind] = useState("footnote");
    const [notePos, setNotePos] = useState(null);
    const [noteDraft, setNoteDraft] = useState("");
    const [noteIsNew, setNoteIsNew] = useState(false);
    // Phase H.13b — clause-picker modal state. `clausePickerMode` is "body"
    // when triggered from the body toolbar (insert HTML at cursor) or
    // "footnote" when triggered from the footnote popover (plain-text
    // replacement, filtered to Footnote/Citation categories).
    const [clausePickerOpen, setClausePickerOpen] = useState(false);
    const [clausePickerMode, setClausePickerMode] = useState("body");
    // Phase N follow-up — content-only heuristic for "this body looks like
    // raw styled HTML that TipTap can't round-trip without dropping data".
    // Shared module so it can be unit-tested independently of the editor.
    const _looksLikeStyledHtml = looksLikeStyledHtml;
    const [tab, setTab] = useState(_looksLikeStyledHtml(initialBodyHtml) ? "html" : "write"); // 'write' | 'html' | 'preview'
    const [htmlDraft, setHtmlDraft] = useState(_looksLikeStyledHtml(initialBodyHtml) ? initialBodyHtml : "");
    // Phase N follow-up — when AI-generated (or any externally-applied raw
    // HTML) content lands via the imperative ref, we hold it byte-for-byte
    // in `htmlDraft`, switch the editor to the HTML source tab, and tell
    // the save flow to persist `htmlDraft` directly instead of round-
    // tripping through TipTap's schema (which strips divs/styles/tables).
    // We also auto-detect raw HTML mode on mount when the initial body
    // looks styled (see `_looksLikeStyledHtml` above) so a tab reload
    // doesn't end up clobbering the saved raw body with TipTap's stripped
    // re-serialisation.
    const [rawHtmlMode, setRawHtmlMode] = useState(_looksLikeStyledHtml(initialBodyHtml));
    const [tagFilter, setTagFilter] = useState("");
    const [tagRegistry, setTagRegistry] = useState([]);
    const [findOpen, setFindOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [imageOpen, setImageOpen] = useState(false);
    const [tableOpen, setTableOpen] = useState(false);
    // Phase H.11 — new UI state.
    const [spellcheck, setSpellcheck] = useState(() => {
        try { return localStorage.getItem("tpleditor_spellcheck") !== "off"; }
        catch { return true; }
    });
    const [autoState, setAutoState] = useState("idle"); // idle|dirty|saving|saved|error|blocked
    const [autoError, setAutoError] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [nowTick, setNowTick] = useState(0);
    // Phase H.11.1 — countdown until next auto-retry; null when no retry pending
    // (either idle, mid-attempt, or terminal "click to retry").
    const [retryAt, setRetryAt] = useState(null);
    const debounceRef = useRef(null);
    const retryRef = useRef(null);
    const lastHashRef = useRef(strHash(initialBodyHtml || ""));
    const retryCountRef = useRef(0);
    const editorWrapRef = useRef(null);
    // Phase H.13d — TipTap v3's `useEditor` does NOT re-render the host
    // component on every transaction by default; selection-only changes
    // (cursor moves) don't trigger a re-render. The heading dropdown +
    // bold/italic etc. toolbar buttons rely on `editor.isActive(...)` to
    // reflect the current cursor context — without this tiny counter we
    // bump on each selection change, those `isActive` reads are read on
    // stale renders and the toolbar's pressed/active states get stuck.
    const [, setSelectionTick] = useState(0);

    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3, 4, 5, 6] },
                // Underline is shipped separately; starter no longer ships it.
            }),
            Underline,
            Subscript,
            Superscript,
            SignatureLineNode,
            // Phase H.12a — section break (block-level divider, no page jump).
            SectionBreakNode,
            // Phase H.12b — footnote / endnote inline atoms.
            FootnoteNode,
            EndnoteNode,
            // Phase H.13d — let headings carry a `class` attribute so we
            // can flag a heading as `numbered-section`; CSS counters do
            // the rendering.
            HeadingClass,
            TextStyle,
            FontSize,
            Color.configure({ types: ["textStyle"] }),
            Highlight.configure({ multicolor: true }),
            FontFamily.configure({ types: ["textStyle"] }),
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            LinkExt.configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { rel: "noopener noreferrer nofollow" },
                validate: (href) => /^https?:\/\//i.test(href),
            }),
            ImageExt.configure({ inline: false, allowBase64: false }),
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
            Placeholder.configure({
                placeholder:
                    "Type your contract content. Click merge tags from the right rail to insert {{tags}}.",
            }),
            CharacterCount.configure({}),
            MergeTagNode,
        ],
        content: loadHtmlForEditor(initialBodyHtml),
    });

    // Fetch merge-tag registry.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/merge-tags");
                setTagRegistry(data.tags || []);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, []);

    const grouped = useMemo(() => {
        const out = {};
        const filter = tagFilter.toLowerCase();
        for (const t of tagRegistry) {
            if (
                filter &&
                !(
                    t.tag.toLowerCase().includes(filter) ||
                    t.label.toLowerCase().includes(filter)
                )
            )
                continue;
            (out[t.group] = out[t.group] || []).push(t);
        }
        return out;
    }, [tagRegistry, tagFilter]);

    // Phase MT-UI — Unfiltered grouping for stable header counts. The
    // accordion always shows every group header (even when search hides
    // all its chips) so the user can scan the catalog at a glance.
    const groupedAll = useMemo(() => {
        const out = {};
        for (const t of tagRegistry) {
            (out[t.group] = out[t.group] || []).push(t);
        }
        return out;
    }, [tagRegistry]);

    // Phase MT-UI — Manual expand/collapse state, persisted per-user in
    // localStorage so the picker layout survives a page reload. Default
    // is "all collapsed" on first ever open so the catalog scans cleanly.
    const MERGE_TAG_EXPANDED_LS_KEY = "rp_merge_tag_picker_expanded_v1";
    const [expandedGroups, setExpandedGroups] = useState(() => {
        try {
            const raw = localStorage.getItem(MERGE_TAG_EXPANDED_LS_KEY);
            if (!raw) return new Set();
            const arr = JSON.parse(raw);
            return new Set(Array.isArray(arr) ? arr : []);
        } catch {
            return new Set();
        }
    });

    const toggleGroup = (g) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(g)) next.delete(g);
            else next.add(g);
            try {
                localStorage.setItem(MERGE_TAG_EXPANDED_LS_KEY, JSON.stringify([...next]));
            } catch {
                /* localStorage quota / disabled — non-fatal, UI still works */
            }
            return next;
        });
    };

    // Phase MT-UI 2 — Bulk expand / collapse helpers. They mutate the
    // persisted manual state; the render still honors the search-override
    // (when ``tagFilter`` is non-empty), so clicking these during an
    // active search updates the "remembered" layout without fighting the
    // search-driven auto-expand.
    const expandAllGroups = () => {
        const all = new Set(Object.keys(groupedAll));
        setExpandedGroups(all);
        try {
            localStorage.setItem(MERGE_TAG_EXPANDED_LS_KEY, JSON.stringify([...all]));
        } catch {
            /* non-fatal */
        }
    };

    const collapseAllGroups = () => {
        setExpandedGroups(new Set());
        try {
            localStorage.setItem(MERGE_TAG_EXPANDED_LS_KEY, JSON.stringify([]));
        } catch {
            /* non-fatal */
        }
    };

    // Phase MT-UI — A group is *displayed* expanded when EITHER the user
    // has manually opened it, OR there's an active search and the group
    // contains a match. The manual state is preserved across search
    // changes, so clearing the search returns the picker to the last
    // hand-toggled layout.
    const isGroupExpanded = (g) => {
        if (tagFilter.trim() && (grouped[g] || []).length > 0) return true;
        return expandedGroups.has(g);
    };

    const knownTags = useMemo(
        () => new Set(tagRegistry.map((t) => t.tag)),
        [tagRegistry]
    );

    // For unknown-tag accounting we need the *current* serialized body,
    // not the editor's live JSON. Use a cheap getHTML each render.
    // Phase N follow-up — when raw HTML mode is on, the preview tab + tag
    // detection should read from htmlDraft (the untouched AI/source body),
    // NOT TipTap's stripped re-serialisation.
    const currentSavedHtml = useMemo(() => {
        if (rawHtmlMode || tab === "html") return htmlDraft || "";
        if (!editor) return "";
        return saveHtmlFromEditor(editor.getHTML());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, editor?.state?.doc, tab, htmlDraft, rawHtmlMode]);

    const detected = useMemo(
        () => parseDetectedTags(currentSavedHtml),
        [currentSavedHtml]
    );
    const unknown = useMemo(
        () => detected.filter((t) => !knownTags.has(t)),
        [detected, knownTags]
    );
    const charCount = editor?.storage?.characterCount?.characters?.() ?? 0;
    const wordCount = editor?.storage?.characterCount?.words?.() ?? 0;
    const stats = useMemo(
        () => computeStats(editor, currentSavedHtml, detected),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editor, currentSavedHtml, detected, charCount, wordCount]
    );

    // Phase H.11 — spellcheck attribute toggling. Persist user pref.
    useEffect(() => {
        if (!editor) return;
        const dom = editor.view.dom;
        dom.setAttribute("spellcheck", spellcheck ? "true" : "false");
        try { localStorage.setItem("tpleditor_spellcheck", spellcheck ? "on" : "off"); } catch (_ignored) {}
    }, [editor, spellcheck]);

    // Phase H.13d — bump the selection tick on every selectionUpdate so
    // toolbar `isActive`/`disabled` reads always see the current cursor
    // context. Without this, TipTap v3's `useEditor` doesn't re-render
    // on cursor moves and the toolbar's pressed states get stuck.
    useEffect(() => {
        if (!editor) return;
        const bump = () => setSelectionTick((n) => (n + 1) | 0);
        editor.on("selectionUpdate", bump);
        editor.on("transaction", bump);
        return () => {
            editor.off("selectionUpdate", bump);
            editor.off("transaction", bump);
        };
    }, [editor]);

    // Phase H.12b — open the footnote/endnote popover when a chip is
    // clicked.  We rely on TipTap's `handleClickOn` rather than a raw DOM
    // listener so we get the ProseMirror doc position for free (which is
    // what we need to patch / delete the node).
    useEffect(() => {
        if (!editor) return;
        const onClickNode = (_view, pos, node, _nodePos, event) => {
            if (node?.type?.name === "footnoteRef") {
                event.preventDefault();
                setNoteKind("footnote");
                setNotePos(pos);
                setNoteDraft(node.attrs?.content || "");
                setNoteIsNew(false);
                // Anchor the popover on the actual chip DOM node so it
                // tracks the cursor across scroll / wrap changes.
                setNoteOpen(_view.nodeDOM(pos));
                return true;
            }
            if (node?.type?.name === "endnoteRef") {
                event.preventDefault();
                setNoteKind("endnote");
                setNotePos(pos);
                setNoteDraft(node.attrs?.content || "");
                setNoteIsNew(false);
                setNoteOpen(_view.nodeDOM(pos));
                return true;
            }
            return false;
        };
        editor.view.someProp("handleClickOn", () => {}); // touch
        editor.on("transaction", () => {}); // no-op
        // Wire via the view's props using on/off pattern: attach our
        // handler to the EditorView with a DOM click listener since v3
        // doesn't expose a public `handleClickOn` setter post-init.
        const dom = editor.view.dom;
        const handler = (e) => {
            const t = e.target;
            if (!(t instanceof Element)) return;
            const chip = t.closest('span.footnote, span.endnote');
            if (!chip) return;
            const pos = editor.view.posAtDOM(chip, 0);
            if (pos == null || pos < 0) return;
            const $pos = editor.state.doc.resolve(pos);
            const node = $pos.nodeAfter || editor.state.doc.nodeAt(pos);
            if (!node) return;
            onClickNode(editor.view, pos, node, pos, e);
        };
        dom.addEventListener("mousedown", handler);
        return () => dom.removeEventListener("mousedown", handler);
    }, [editor]);

    // Phase H.12b — insert a fresh chip and immediately open the popover so
    // the admin can type content. Cancelling without saving deletes the chip.
    const handleInsertNote = (kind) => {
        if (!editor) return;
        const cmd = kind === "endnote" ? "insertEndnote" : "insertFootnote";
        editor.chain().focus()[cmd]("").run();
        // The newly inserted node now sits at `selection.from - 1`.
        const pos = Math.max(0, editor.state.selection.from - 1);
        const node = editor.state.doc.nodeAt(pos);
        if (!node) return;
        setNoteKind(kind);
        setNotePos(pos);
        setNoteDraft("");
        setNoteIsNew(true);
        // Defer the popover anchoring one tick so the DOM node exists.
        setTimeout(() => {
            const dom = editor.view.nodeDOM(pos);
            if (dom) setNoteOpen(dom);
        }, 0);
    };

    const handleSaveNote = () => {
        if (!editor || notePos == null) return;
        const tr = editor.state.tr;
        const node = editor.state.doc.nodeAt(notePos);
        if (!node) { setNoteOpen(null); return; }
        tr.setNodeAttribute(notePos, "content", noteDraft);
        editor.view.dispatch(tr);
        setNoteOpen(null);
        setNoteIsNew(false);
    };

    const handleDeleteNote = () => {
        if (!editor || notePos == null) return;
        editor.chain().focus().deleteRange({ from: notePos, to: notePos + 1 }).run();
        setNoteOpen(null);
        setNoteIsNew(false);
    };

    const handleCancelNote = () => {
        // Newly inserted but never saved → remove the placeholder chip so
        // the doc doesn't get littered with empty notes.
        if (noteIsNew) handleDeleteNote();
        else setNoteOpen(null);
    };

    // Phase H.11 — live "Saved Xs ago" elapsed counter.
    // Phase H.11.1 — also tick while a retry is pending so the countdown updates.
    useEffect(() => {
        const needsTick = (autoState === "saved" && savedAt) || retryAt;
        if (!needsTick) return;
        const id = setInterval(() => setNowTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [autoState, savedAt, retryAt]);

    // Phase H.11.1 — required-field gate. Returns {valid, reason}.
    // Right now the only hard-required field is template name.
    const validateForm = () => {
        if (!name.trim()) return { valid: false, reason: "Template name is required" };
        return { valid: true };
    };

    // Phase H.11 — debounced auto-save state machine.
    // Phase H.11.1 — auto-save backoff: 3 retries at 5s, 15s, 60s, then
    // terminal "Save failed — click to retry" until the user clicks retry.
    const AUTO_SAVE_BACKOFFS_MS = [5000, 15000, 60000];

    const flushAutoSave = async () => {
        if (!editor || !onAutoSave) return;
        // Phase H.11.1 — required-field gate. Surface a visible "blocked"
        // pill rather than silently no-op, and cancel any pending retry.
        const v = validateForm();
        if (!v.valid) {
            setAutoError(v.reason);
            setAutoState("blocked");
            setRetryAt(null);
            if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
            return;
        }
        // Phase N follow-up — when raw-HTML mode is active OR the user is
        // actively editing the HTML source tab, persist the raw draft
        // byte-for-byte instead of TipTap's stripped re-serialisation.
        const html = (rawHtmlMode || tab === "html")
            ? (htmlDraft || "")
            : saveHtmlFromEditor(editor.getHTML());
        const h = strHash(html + "|" + name + "|" + category + "|" + description + "|" + (headerHtml || "") + "|" + (footerHtml || ""));
        if (h === lastHashRef.current) {
            setAutoState((s) => (s === "dirty" ? "saved" : s));
            return;
        }
        setAutoState("saving");
        setRetryAt(null);
        try {
            await onAutoSave({
                name: name.trim(),
                category: category.trim(),
                description: description.trim(),
                body_html: html,
                header_html: headerHtml || "",
                footer_html: footerHtml || "",
                // Phase H.13a — flag this PATCH as an auto-save so the
                // backend applies the 30s version-history throttle.
                _trigger: "auto",
            });
            lastHashRef.current = h;
            retryCountRef.current = 0;
            setAutoError(null);
            setRetryAt(null);
            setSavedAt(Date.now());
            setAutoState("saved");
            setPersistedBodyHtml(html);
        } catch (err) {
            const attempt = retryCountRef.current;
            retryCountRef.current = attempt + 1;
            const detail = err?.response?.data?.detail || err?.message || "Save failed";
            setAutoError(detail);
            setAutoState("error");
            if (attempt < AUTO_SAVE_BACKOFFS_MS.length) {
                const ms = AUTO_SAVE_BACKOFFS_MS[attempt];
                setRetryAt(Date.now() + ms);
                if (retryRef.current) clearTimeout(retryRef.current);
                retryRef.current = setTimeout(() => {
                    setRetryAt(null);
                    flushAutoSave();
                }, ms);
            } else {
                // All 3 retries exhausted — hold "click to retry" terminal state.
                setRetryAt(null);
                if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
            }
        }
    };

    // Trigger debounced auto-save whenever the editor doc OR metadata fields
    // change. We rely on the editor's `transaction` updates via state.doc.
    useEffect(() => {
        if (!editor || !onAutoSave) return;
        const tick = () => {
            // Phase H.11.1 — if a required field is missing, switch to the
            // "blocked" pill state IMMEDIATELY and skip the debounce + PATCH
            // entirely. Once the field is restored, the metadata useEffect
            // re-fires and recovers the normal dirty → saving → saved flow.
            const v = validateForm();
            if (!v.valid) {
                setAutoState("blocked");
                setAutoError(v.reason);
                if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
                if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
                setRetryAt(null);
                return;
            }
            // Skip transactions that don't change the doc (selection-only).
            setAutoState((s) => (s === "saving" ? s : "dirty"));
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => flushAutoSave(), 2000);
        };
        editor.on("update", tick);
        return () => {
            editor.off("update", tick);
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, onAutoSave, name, category, description]);

    // Metadata-field edits also trigger debounced save (same path).
    useEffect(() => {
        if (!editor || !onAutoSave) return;
        // Phase H.11.1 — required-field gate also applies to metadata edits.
        const v = validateForm();
        if (!v.valid) {
            setAutoState("blocked");
            setAutoError(v.reason);
            if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
            if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
            setRetryAt(null);
            return;
        }
        setAutoState((s) => (s === "saving" ? s : "dirty"));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => flushAutoSave(), 2000);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, category, description, headerHtml, footerHtml, onAutoSave]);

    // Phase N follow-up — raw HTML draft edits (HTML source tab OR an
    // externally-applied AI body via the imperative ref) also trigger the
    // debounced auto-save so the textarea ↔ DB stays in sync without the
    // user having to click "Save" manually.
    useEffect(() => {
        if (!editor || !onAutoSave) return;
        if (!rawHtmlMode && tab !== "html") return;
        const v = validateForm();
        if (!v.valid) {
            setAutoState("blocked");
            setAutoError(v.reason);
            return;
        }
        setAutoState((s) => (s === "saving" ? s : "dirty"));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => flushAutoSave(), 1200);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlDraft, rawHtmlMode, tab]);

    // Phase H.11 — keyboard: Enter inside code_block stays in; Shift+Enter exits.
    useEffect(() => {
        if (!editor) return;
        const handler = (e) => {
            if (e.shiftKey && e.key === "Enter" && editor.isActive("codeBlock")) {
                e.preventDefault();
                editor.chain().focus().exitCode().run();
            }
        };
        const dom = editor.view.dom;
        dom.addEventListener("keydown", handler);
        return () => dom.removeEventListener("keydown", handler);
    }, [editor]);

    // ------------------------------ Actions ------------------------------

    const handleSubmit = async () => {
        // Phase H.11.1 — manual save honors the same required-field gate as
        // auto-save. Toast the user and reflect the blocked state in the pill.
        const v = validateForm();
        if (!v.valid) {
            toast.error(`Please fill in required fields — ${v.reason}`);
            setAutoState("blocked");
            setAutoError(v.reason);
            return;
        }
        if (!editor) return;
        // Phase H.11 — cancel pending debounce to avoid double-saves.
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (retryRef.current) clearTimeout(retryRef.current);
        // Phase N follow-up — same raw-HTML escape hatch as the auto-save.
        const html = (rawHtmlMode || tab === "html")
            ? (htmlDraft || "")
            : saveHtmlFromEditor(editor.getHTML());
        await onSave({
            name: name.trim(),
            category: category.trim(),
            description: description.trim(),
            body_html: html,
            header_html: headerHtml || "",
            footer_html: footerHtml || "",
            // Phase H.13a — manual save bypasses the auto-version throttle.
            _trigger: "manual",
        });
        lastHashRef.current = strHash(
            html + "|" + name + "|" + category + "|" + description + "|" + (headerHtml || "") + "|" + (footerHtml || "")
        );
        setPersistedBodyHtml(html);
        retryCountRef.current = 0;
        setAutoError(null);
        setSavedAt(Date.now());
        setAutoState("saved");
    };

    const handleInsertTag = (path) => {
        if (!editor) return;
        editor.chain().focus().insertMergeTag(path).run();
    };

    const handleSwitchTab = (next) => {
        if (!editor) {
            setTab(next);
            return;
        }
        if (tab === "html" && next === "write") {
            // Phase N follow-up — confirm warning when leaving raw HTML
            // mode. TipTap's parser strips inline styles / divs / table
            // attrs that the AI generator (or hand-edited source) relies
            // on, so we make the data loss explicit.
            if (rawHtmlMode) {
                const proceed = window.confirm(
                    "Switching to the WYSIWYG editor may strip inline styles, custom HTML tags, " +
                    "and table attributes that aren't in the editor's schema. The currently-saved HTML " +
                    "source will stay intact only if you don't edit it in WYSIWYG mode.\n\n" +
                    "Continue and re-parse through the editor?"
                );
                if (!proceed) return;
                setRawHtmlMode(false);
            }
            // HTML draft → WYSIWYG: parse the draft as new content
            editor.commands.setContent(loadHtmlForEditor(htmlDraft), false);
        }
        if (next === "html" && tab !== "html") {
            // Only seed the draft from the editor when we don't already
            // have raw HTML waiting (i.e. AI Apply already populated it).
            if (!rawHtmlMode) {
                setHtmlDraft(saveHtmlFromEditor(editor.getHTML()));
            }
        }
        setTab(next);
    };

    // Phase N follow-up — imperative hook the parent uses to apply
    // AI-generated body HTML byte-for-byte. We:
    //   1. populate `htmlDraft` with the raw HTML (no TipTap round-trip)
    //   2. flip to the HTML source tab
    //   3. raise `rawHtmlMode` so subsequent saves bypass TipTap
    //   4. seed the editor with a placeholder so the dirty hash actually
    //      differs from the persisted body (auto-save kicks in)
    useImperativeHandle(_ref, () => ({
        applyRawBodyHtml: (html) => {
            const raw = String(html || "");
            setHtmlDraft(raw);
            setRawHtmlMode(true);
            setTab("html");
            // Mark the new content as dirty so auto-save fires + the
            // Save button enables. (Don't push raw through editor —
            // that's the whole point of this bypass.)
            lastHashRef.current = "";
            setAutoState("dirty");
        },
    }), []);

    // ------------------------------ Toolbar buttons ------------------------------

    const ed = editor;

    // Phase H.13d — derived for the "Numbered article" toolbar toggle.
    // `isInHeading` enables/disables the button; `numberedActive` drives
    // the active-state pill. Recomputed on every render because the
    // editor's update event already re-renders this component.
    const isInHeading = ed?.isActive?.("heading") ?? false;
    const numberedActive = isNumberedSectionActive(ed);

    // ------------------------------ Render ------------------------------

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            <div ref={editorWrapRef}>
                <Card>
                    <CardContent className="p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="sm:col-span-2 space-y-2">
                                <Label htmlFor="tpl_name">Template name</Label>
                                <Input id="tpl_name" data-testid="template-name-input"
                                    value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Standard equipment lease" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="tpl_category">Category</Label>
                                <Input id="tpl_category" data-testid="template-category-input"
                                    value={category} onChange={(e) => setCategory(e.target.value)}
                                    placeholder="e.g. lease, addendum" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="tpl_desc">Description</Label>
                            <Input id="tpl_desc" data-testid="template-description-input"
                                value={description} onChange={(e) => setDescription(e.target.value)}
                                placeholder="Optional description for your team" />
                        </div>
                    </CardContent>
                </Card>

                {/* Phase H.12a — Document header & footer (collapsible).
                    Default-collapsed unless the template already has content;
                    edits flow through the same auto-save debounce as the body. */}
                <Card className="mt-4" data-testid="header-footer-card">
                    <CardContent className="p-0">
                        <button type="button"
                            data-testid="header-footer-toggle"
                            aria-expanded={headerFooterOpen}
                            onClick={() => setHeaderFooterOpen((o) => !o)}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                            {headerFooterOpen
                                ? <ChevronDown size={14} className="text-slate-500" />
                                : <ChevronRight size={14} className="text-slate-500" />}
                            <span className="text-sm font-medium text-slate-900">Header &amp; footer</span>
                            <span className="text-xs text-slate-500">
                                {headerFooterOpen
                                    ? "Click to collapse"
                                    : (headerHtml || footerHtml)
                                        ? "Set — click to edit"
                                        : "Optional — repeats on every PDF page"}
                            </span>
                        </button>
                        {headerFooterOpen && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-4 pb-4">
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-slate-500">Running header</Label>
                                    <MiniEditor
                                        mode="header"
                                        initialHtml={initialHeaderHtml}
                                        onChange={setHeaderHtml}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wider text-slate-500">Running footer</Label>
                                    <MiniEditor
                                        mode="footer"
                                        initialHtml={initialFooterHtml}
                                        onChange={setFooterHtml}
                                    />
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardContent className="p-0">
                        {/* Sticky toolbar */}
                        <div
                            className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-slate-200 px-3 py-2 bg-white"
                            data-testid="editor-toolbar">
                            {/* Group 1 — Headings (dropdown so H1-H6 fit) */}
                            <select
                                aria-label="Block type"
                                data-testid="tb-heading-select"
                                className="h-8 text-xs rounded-md border border-slate-200 bg-white px-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                value={(() => {
                                    if (!ed) return "p";
                                    for (let lvl = 1; lvl <= 6; lvl++)
                                        if (ed.isActive("heading", { level: lvl })) return `h${lvl}`;
                                    return "p";
                                })()}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (!ed) return;
                                    if (v === "p") ed.chain().focus().setParagraph().run();
                                    else
                                        ed.chain().focus()
                                            .toggleHeading({ level: parseInt(v.slice(1), 10) }).run();
                                }}>
                                {HEADING_OPTIONS.map((h) => (
                                    <option key={h.v} value={h.v}>{h.label}</option>
                                ))}
                            </select>
                            {/* Phase H.13d — Numbered article toggle. Only
                                meaningful when the cursor is inside a
                                heading; otherwise we disable the button. */}
                            <TbBtn
                                title={isInHeading
                                    ? (numberedActive ? "Remove numbering from this article/section" : "Number this article/section")
                                    : "Place cursor inside a heading first"}
                                testid="tb-numbered-section"
                                active={numberedActive}
                                disabled={!isInHeading}
                                onClick={() => ed?.chain().focus().toggleNumberedSection().run()}>
                                <Hash size={16} />
                            </TbBtn>
                            <Divider />

                            {/* Group 2 — Inline formatting */}
                            <TbBtn title="Bold" testid="tb-bold"
                                active={ed?.isActive("bold")}
                                onClick={() => ed?.chain().focus().toggleBold().run()}>
                                <Bold size={16} />
                            </TbBtn>
                            <TbBtn title="Italic" testid="tb-italic"
                                active={ed?.isActive("italic")}
                                onClick={() => ed?.chain().focus().toggleItalic().run()}>
                                <Italic size={16} />
                            </TbBtn>
                            <TbBtn title="Underline" testid="tb-underline"
                                active={ed?.isActive("underline")}
                                onClick={() => ed?.chain().focus().toggleUnderline().run()}>
                                <UnderlineIcon size={16} />
                            </TbBtn>
                            <TbBtn title="Strikethrough" testid="tb-strike"
                                active={ed?.isActive("strike")}
                                onClick={() => ed?.chain().focus().toggleStrike().run()}>
                                <Strikethrough size={16} />
                            </TbBtn>
                            <TbBtn title="Inline code" testid="tb-code"
                                active={ed?.isActive("code")}
                                onClick={() => ed?.chain().focus().toggleCode().run()}>
                                <Code size={16} />
                            </TbBtn>
                            <TbBtn title="Subscript" testid="tb-sub"
                                active={ed?.isActive("subscript")}
                                onClick={() => {
                                    if (!ed) return;
                                    // Phase H.11 — mutually exclusive with superscript.
                                    // Combine into ONE chain so the unset + toggle
                                    // run as a single transaction (separate chains
                                    // lose selection between commands).
                                    ed.chain().focus().unsetSuperscript().toggleSubscript().run();
                                }}>
                                <SubIcon size={16} />
                            </TbBtn>
                            <TbBtn title="Superscript" testid="tb-sup"
                                active={ed?.isActive("superscript")}
                                onClick={() => {
                                    if (!ed) return;
                                    ed.chain().focus().unsetSubscript().toggleSuperscript().run();
                                }}>
                                <SupIcon size={16} />
                            </TbBtn>
                            <TbBtn title="Code block" testid="tb-codeblock"
                                active={ed?.isActive("codeBlock")}
                                onClick={() => ed?.chain().focus().toggleCodeBlock().run()}>
                                <FileCode2 size={16} />
                            </TbBtn>
                            <Divider />

                            {/* Group 3 — Font family / size / colors */}
                            <select
                                aria-label="Font family"
                                data-testid="tb-font-family"
                                className="h-8 text-xs rounded-md border border-slate-200 bg-white px-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                value={ed?.getAttributes("textStyle")?.fontFamily || ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (!v) ed?.chain().focus().unsetFontFamily().run();
                                    else ed?.chain().focus().setFontFamily(v).run();
                                }}>
                                {FONT_FAMILIES.map((f) => (
                                    <option key={f.label} value={f.value}>{f.label}</option>
                                ))}
                            </select>
                            <select
                                aria-label="Font size"
                                data-testid="tb-font-size"
                                className="h-8 text-xs rounded-md border border-slate-200 bg-white px-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                value={ed?.getAttributes("textStyle")?.fontSize || ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (!v) ed?.chain().focus().unsetFontSize().run();
                                    else ed?.chain().focus().setFontSize(v).run();
                                }}>
                                <option value="">Size</option>
                                {FONT_SIZES.map((s) => (
                                    <option key={s} value={s}>{s.replace("pt", "")}</option>
                                ))}
                            </select>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        title="Text color" aria-label="Text color"
                                        data-testid="tb-color"
                                        className="h-8 px-1.5 inline-flex items-center justify-center rounded-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <Palette size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-44 p-2">
                                    <div className="grid grid-cols-4 gap-1.5" data-testid="tb-color-palette">
                                        {TEXT_COLORS.map((c) => (
                                            <button key={c.v} type="button" title={c.label}
                                                onClick={() => ed?.chain().focus().setColor(c.v).run()}
                                                className="h-7 w-7 rounded border border-slate-200 hover:scale-110 transition-transform"
                                                style={{ backgroundColor: c.v }} />
                                        ))}
                                        <button type="button" title="No color"
                                            data-testid="tb-color-reset"
                                            onClick={() => ed?.chain().focus().unsetColor().run()}
                                            className="h-7 w-7 rounded border border-slate-300 bg-white text-[10px] text-slate-500">
                                            None
                                        </button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        title="Highlight" aria-label="Highlight"
                                        data-testid="tb-highlight"
                                        className="h-8 px-1.5 inline-flex items-center justify-center rounded-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <Highlighter size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-44 p-2">
                                    <div className="grid grid-cols-4 gap-1.5" data-testid="tb-highlight-palette">
                                        {HIGHLIGHT_COLORS.map((c) => (
                                            <button key={c.v} type="button" title={c.label}
                                                onClick={() => ed?.chain().focus().toggleHighlight({ color: c.v }).run()}
                                                className="h-7 w-7 rounded border border-slate-200 hover:scale-110 transition-transform"
                                                style={{ backgroundColor: c.v }} />
                                        ))}
                                        <button type="button" title="No highlight"
                                            data-testid="tb-highlight-reset"
                                            onClick={() => ed?.chain().focus().unsetHighlight().run()}
                                            className="h-7 w-7 rounded border border-slate-300 bg-white text-[10px] text-slate-500">
                                            None
                                        </button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Divider />

                            {/* Group 4 — Alignment */}
                            <TbBtn title="Align left" testid="tb-align-left"
                                active={ed?.isActive({ textAlign: "left" })}
                                onClick={() => ed?.chain().focus().setTextAlign("left").run()}>
                                <AlignLeft size={16} />
                            </TbBtn>
                            <TbBtn title="Align center" testid="tb-align-center"
                                active={ed?.isActive({ textAlign: "center" })}
                                onClick={() => ed?.chain().focus().setTextAlign("center").run()}>
                                <AlignCenter size={16} />
                            </TbBtn>
                            <TbBtn title="Align right" testid="tb-align-right"
                                active={ed?.isActive({ textAlign: "right" })}
                                onClick={() => ed?.chain().focus().setTextAlign("right").run()}>
                                <AlignRight size={16} />
                            </TbBtn>
                            <TbBtn title="Justify" testid="tb-align-justify"
                                active={ed?.isActive({ textAlign: "justify" })}
                                onClick={() => ed?.chain().focus().setTextAlign("justify").run()}>
                                <AlignJustify size={16} />
                            </TbBtn>
                            <Divider />

                            {/* Group 5 — Lists */}
                            <TbBtn title="Bullet list" testid="tb-ul"
                                active={ed?.isActive("bulletList")}
                                onClick={() => ed?.chain().focus().toggleBulletList().run()}>
                                <List size={16} />
                            </TbBtn>
                            <TbBtn title="Numbered list" testid="tb-ol"
                                active={ed?.isActive("orderedList")}
                                onClick={() => ed?.chain().focus().toggleOrderedList().run()}>
                                <ListOrdered size={16} />
                            </TbBtn>
                            <TbBtn title="Blockquote" testid="tb-quote"
                                active={ed?.isActive("blockquote")}
                                onClick={() => ed?.chain().focus().toggleBlockquote().run()}>
                                <Quote size={16} />
                            </TbBtn>
                            <TbBtn title="Indent (list item)" testid="tb-indent"
                                disabled={!ed?.can().sinkListItem("listItem")}
                                onClick={() => ed?.chain().focus().sinkListItem("listItem").run()}>
                                <IndentIncrease size={16} />
                            </TbBtn>
                            <TbBtn title="Outdent (list item)" testid="tb-outdent"
                                disabled={!ed?.can().liftListItem("listItem")}
                                onClick={() => ed?.chain().focus().liftListItem("listItem").run()}>
                                <IndentDecrease size={16} />
                            </TbBtn>
                            <Divider />

                            {/* Group 6 — Insert */}
                            <Popover open={linkOpen} onOpenChange={setLinkOpen}>
                                <PopoverTrigger asChild>
                                    <button type="button" title="Insert link" aria-label="Insert link"
                                        data-testid="tb-link"
                                        className={`h-8 px-1.5 inline-flex items-center justify-center rounded-md border ${ed?.isActive("link") ? "bg-blue-900 text-white border-blue-900" : "border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200"} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}>
                                        <LinkIcon size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-3" data-testid="link-popover">
                                    <LinkPopover editor={ed} onDone={() => setLinkOpen(false)} />
                                </PopoverContent>
                            </Popover>
                            <Popover open={imageOpen} onOpenChange={setImageOpen}>
                                <PopoverTrigger asChild>
                                    <button type="button" title="Insert image (URL)" aria-label="Insert image"
                                        data-testid="tb-image"
                                        className="h-8 px-1.5 inline-flex items-center justify-center rounded-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <ImageIcon size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-3" data-testid="image-popover">
                                    <ImagePopover editor={ed} onDone={() => setImageOpen(false)} />
                                </PopoverContent>
                            </Popover>
                            <Popover open={tableOpen} onOpenChange={setTableOpen}>
                                <PopoverTrigger asChild>
                                    <button type="button" title="Insert table" aria-label="Insert table"
                                        data-testid="tb-table"
                                        className="h-8 px-1.5 inline-flex items-center justify-center rounded-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <TableIcon size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-3" data-testid="table-popover">
                                    <TablePicker editor={ed} onDone={() => setTableOpen(false)} />
                                </PopoverContent>
                            </Popover>
                            <TbBtn title="Horizontal rule" testid="tb-hr"
                                onClick={() => ed?.chain().focus().setHorizontalRule().run()}>
                                <Minus size={16} />
                            </TbBtn>
                            <TbBtn title="Page break" testid="tb-pagebreak"
                                onClick={() => ed?.chain().focus().insertContent(
                                    '<hr class="page-break" style="page-break-before:always;break-before:page;border:none;height:0;margin:0" />'
                                ).run()}>
                                <FileText size={16} />
                            </TbBtn>
                            {/* Phase H.12a — section break (no page jump). */}
                            <TbBtn title="Section break (visual divider, no page jump)" testid="tb-section-break"
                                onClick={() => ed?.chain().focus().insertSectionBreak().run()}>
                                <SeparatorHorizontal size={16} />
                            </TbBtn>
                            {/* Phase H.12b — footnote / endnote. Body-only. */}
                            <TbBtn title="Insert footnote (page-bottom)" testid="tb-footnote"
                                onClick={() => handleInsertNote("footnote")}>
                                <Asterisk size={16} />
                            </TbBtn>
                            <TbBtn title="Insert endnote (collected at end of doc)" testid="tb-endnote"
                                onClick={() => handleInsertNote("endnote")}>
                                <BookmarkPlus size={16} />
                            </TbBtn>
                            {/* Phase H.13b — Insert clause from library. */}
                            <TbBtn title="Insert clause from library" testid="tb-insert-clause"
                                onClick={() => { setClausePickerMode("body"); setClausePickerOpen(true); }}>
                                <BookOpen size={16} />
                            </TbBtn>

                            {/* Phase H.11 — Today's date split button + format menu */}
                            <span className="inline-flex">
                                <button type="button"
                                    title="Insert today's date (long format)"
                                    aria-label="Insert today's date"
                                    data-testid="tb-today"
                                    onClick={() => ed?.chain().focus().insertContent(fmtToday("long")).run()}
                                    className="h-8 px-1.5 inline-flex items-center justify-center rounded-l-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                    <CalendarDays size={16} />
                                </button>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button type="button" title="Pick date format"
                                            aria-label="Date format"
                                            data-testid="tb-today-format"
                                            className="h-8 px-0.5 inline-flex items-center justify-center rounded-r-md border border-transparent text-slate-500 hover:bg-slate-100 hover:border-slate-200">
                                            <ChevronDown size={12} />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-48 p-1" data-testid="today-format-menu">
                                        {[
                                            { kind: "long", label: fmtToday("long") },
                                            { kind: "short", label: fmtToday("short") },
                                            { kind: "iso", label: fmtToday("iso") },
                                        ].map((opt) => (
                                            <button type="button" key={opt.kind}
                                                data-testid={`today-fmt-${opt.kind}`}
                                                onClick={() => ed?.chain().focus().insertContent(fmtToday(opt.kind)).run()}
                                                className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-100">
                                                <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-2">{opt.kind}</span>
                                                {opt.label}
                                            </button>
                                        ))}
                                    </PopoverContent>
                                </Popover>
                            </span>

                            {/* Phase H.11 — special-character popover */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button type="button"
                                        title="Insert special character"
                                        aria-label="Insert special character"
                                        data-testid="tb-special-char"
                                        className="h-8 px-1.5 inline-flex items-center justify-center rounded-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <Sigma size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-3 max-h-[60vh] overflow-y-auto" data-testid="special-char-popover">
                                    {SPECIAL_CHARS.map((sec) => (
                                        <div key={sec.group} className="mb-2.5 last:mb-0">
                                            <p className="text-[10px] tracking-wider uppercase text-slate-500 mb-1">{sec.group}</p>
                                            <div className="grid grid-cols-8 gap-1">
                                                {sec.chars.map((ch) => (
                                                    <button type="button" key={ch}
                                                        data-testid={`special-char-${ch.charCodeAt(0)}`}
                                                        onClick={() => ed?.chain().focus().insertContent(ch).run()}
                                                        className="h-8 w-8 inline-flex items-center justify-center rounded border border-slate-200 bg-white text-sm hover:bg-slate-100">
                                                        {ch}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </PopoverContent>
                            </Popover>

                            {/* Phase H.11 — signature line block */}
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button type="button"
                                        title="Insert signature line (printed copy)"
                                        aria-label="Insert signature line"
                                        data-testid="tb-signature-line"
                                        className="h-8 px-1.5 inline-flex items-center justify-center rounded-md border border-transparent text-slate-700 hover:bg-slate-100 hover:border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <PenLine size={16} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-2" data-testid="signature-line-menu">
                                    <p className="text-[10px] tracking-wider uppercase text-slate-500 mb-1 px-1">Label</p>
                                    {SIGNATURE_LABELS.map((lbl) => (
                                        <button type="button" key={lbl}
                                            data-testid={`sig-label-${lbl.toLowerCase().replace(/\W+/g, "-")}`}
                                            onClick={() => ed?.chain().focus().insertSignatureLine(lbl).run()}
                                            className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-100">
                                            {lbl}
                                        </button>
                                    ))}
                                    <button type="button" data-testid="sig-label-custom"
                                        onClick={() => {
                                            const custom = window.prompt("Custom signature label:");
                                            if (custom && custom.trim())
                                                ed?.chain().focus().insertSignatureLine(custom.trim()).run();
                                        }}
                                        className="w-full text-left text-xs italic px-2 py-1.5 rounded hover:bg-slate-100 text-slate-600">
                                        Custom…
                                    </button>
                                </PopoverContent>
                            </Popover>
                            <Divider />

                            {/* Group 7 — History */}
                            <TbBtn title="Undo" testid="tb-undo"
                                disabled={!ed?.can()?.undo?.()}
                                onClick={() => ed?.chain().focus().undo().run()}>
                                <Undo2 size={16} />
                            </TbBtn>
                            <TbBtn title="Redo" testid="tb-redo"
                                disabled={!ed?.can()?.redo?.()}
                                onClick={() => ed?.chain().focus().redo().run()}>
                                <Redo2 size={16} />
                            </TbBtn>
                            <Divider />

                            {/* Group 8 — Utilities */}
                            <TbBtn title="Clear formatting" testid="tb-clear"
                                onClick={() => ed?.chain().focus().unsetAllMarks().clearNodes().run()}>
                                <Eraser size={16} />
                            </TbBtn>
                            <TbBtn title="Find & Replace" testid="tb-find"
                                onClick={() => setFindOpen(true)}>
                                <Search size={16} />
                            </TbBtn>
                            <TbBtn title={spellcheck ? "Spell check: on" : "Spell check: off"} testid="tb-spellcheck"
                                active={spellcheck}
                                onClick={() => setSpellcheck((s) => !s)}>
                                <SpellCheck2 size={16} />
                            </TbBtn>
                            <TbBtn title="Print preview" testid="tb-print"
                                onClick={() => openPrintPreview(currentSavedHtml, name)}>
                                <Printer size={16} />
                            </TbBtn>

                            {/* Group 9 — View (right-aligned) */}
                            <div className="ml-auto inline-flex items-center gap-2">
                                {/* Phase H.13a — Version history button. Only
                                    renders when we have a templateId (i.e.,
                                    editing an existing template). New drafts
                                    have no history to show. */}
                                {templateId && (
                                    <button type="button"
                                        data-testid="version-history-button"
                                        title="Version history"
                                        onClick={() => setVersionDrawerOpen(true)}
                                        className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50">
                                        <History size={12} /> History
                                    </button>
                                )}
                                {/* Phase H.12a — Export bar. PDF requires the
                                    template to already exist (so we have an id
                                    to hit /export-pdf with). HTML & Markdown
                                    work even on a brand-new draft. */}
                                <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white"
                                    data-testid="editor-export-bar">
                                    <button type="button"
                                        data-testid="export-html-button"
                                        title="Export as standalone HTML file"
                                        onClick={() => exportTemplateAsHtml({
                                            name: name || "template",
                                            headerHtml: headerHtml,
                                            bodyHtml: saveHtmlFromEditor(editor ? editor.getHTML() : ""),
                                            footerHtml: footerHtml,
                                        })}
                                        className="px-2 h-7 text-xs font-medium rounded inline-flex items-center gap-1 text-slate-700 hover:bg-slate-50">
                                        <FileCode size={12} /> HTML
                                    </button>
                                    <button type="button"
                                        data-testid="export-markdown-button"
                                        title="Export as Markdown (.md)"
                                        onClick={() => exportTemplateAsMarkdown({
                                            name: name || "template",
                                            headerHtml: headerHtml,
                                            bodyHtml: saveHtmlFromEditor(editor ? editor.getHTML() : ""),
                                            footerHtml: footerHtml,
                                        })}
                                        className="px-2 h-7 text-xs font-medium rounded inline-flex items-center gap-1 text-slate-700 hover:bg-slate-50">
                                        <FileType2 size={12} /> Markdown
                                    </button>
                                    <button type="button"
                                        data-testid="export-pdf-button"
                                        title={templateId ? "Export as PDF (placeholder values)" : "Save the template first to enable PDF export"}
                                        disabled={!templateId || exportingPdf}
                                        onClick={async () => {
                                            if (!templateId) return;
                                            setExportingPdf(true);
                                            try {
                                                const resp = await api.get(`/admin/templates/${templateId}/export-pdf`, { responseType: "blob" });
                                                const fname = (name || "template").replace(/[^A-Za-z0-9._-]+/g, "_") + ".pdf";
                                                const url = URL.createObjectURL(resp.data);
                                                const a = document.createElement("a");
                                                a.href = url;
                                                a.download = fname;
                                                document.body.appendChild(a);
                                                a.click();
                                                setTimeout(() => {
                                                    document.body.removeChild(a);
                                                    URL.revokeObjectURL(url);
                                                }, 0);
                                            } catch (err) {
                                                toast.error(formatApiError(err));
                                            } finally {
                                                setExportingPdf(false);
                                            }
                                        }}
                                        className="px-2 h-7 text-xs font-medium rounded inline-flex items-center gap-1 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                        {exportingPdf ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} PDF
                                    </button>
                                </div>
                                <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white" role="tablist">
                                    <button type="button" onClick={() => handleSwitchTab("write")}
                                        data-testid="editor-tab-write"
                                        className={`px-3 h-7 text-xs font-medium rounded inline-flex items-center gap-1.5 ${tab === "write" ? "bg-blue-900 text-white" : "text-slate-600"}`}>
                                        <Type size={12} /> Editor
                                    </button>
                                    <button type="button" onClick={() => handleSwitchTab("html")}
                                        data-testid="editor-tab-html"
                                        className={`px-3 h-7 text-xs font-medium rounded inline-flex items-center gap-1.5 ${tab === "html" ? "bg-blue-900 text-white" : "text-slate-600"}`}>
                                        <Code2 size={12} /> HTML
                                    </button>
                                    <button type="button" onClick={() => handleSwitchTab("preview")}
                                        data-testid="editor-tab-preview"
                                        className={`px-3 h-7 text-xs font-medium rounded inline-flex items-center gap-1.5 ${tab === "preview" ? "bg-blue-900 text-white" : "text-slate-600"}`}>
                                        <Eye size={12} /> Preview
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        {tab === "write" && (
                            <div className="min-h-[480px] p-6 bg-white"
                                data-testid="template-body-editor">
                                <EditorContent editor={ed} className="prose prose-slate max-w-none focus:outline-none" />
                            </div>
                        )}

                        {/* Phase H.12b — footnote / endnote popover.
                            Anchored to the chip DOM node via a virtual ref so
                            it stays attached as the user scrolls / resizes. */}
                        {noteOpen && (
                            <Popover
                                open={!!noteOpen}
                                onOpenChange={(o) => { if (!o) handleCancelNote(); }}
                            >
                                <PopoverTrigger asChild>
                                    {/* Virtual anchor — Radix needs a child but we
                                        position via PopoverContent's collision logic
                                        relative to the chip's bounding box. */}
                                    <span
                                        ref={(el) => {
                                            if (!el || !noteOpen) return;
                                            const rect = noteOpen.getBoundingClientRect();
                                            el.style.position = "fixed";
                                            el.style.top = `${rect.bottom}px`;
                                            el.style.left = `${rect.left}px`;
                                            el.style.width = "0px";
                                            el.style.height = "0px";
                                            el.style.pointerEvents = "none";
                                        }}
                                        aria-hidden="true"
                                    />
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-80 p-3"
                                    data-testid={`note-popover-${noteKind}`}
                                    side="bottom"
                                    align="start"
                                    sideOffset={4}
                                >
                                    <p className="text-xs font-medium tracking-wider uppercase text-slate-500 mb-2">
                                        {noteKind === "endnote" ? "Endnote" : "Footnote"} content
                                    </p>
                                    <textarea
                                        autoFocus
                                        data-testid="note-popover-input"
                                        value={noteDraft}
                                        onChange={(e) => setNoteDraft(e.target.value)}
                                        placeholder={noteKind === "endnote"
                                            ? "e.g. Smith et al., Rental Law Review (2019)."
                                            : "e.g. As defined in Section 1.2."}
                                        className="w-full min-h-[72px] text-sm rounded-md border border-slate-200 px-2 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                                e.preventDefault();
                                                handleSaveNote();
                                            }
                                            if (e.key === "Escape") {
                                                e.preventDefault();
                                                handleCancelNote();
                                            }
                                        }}
                                    />
                                    {noteDraft.length > 500 && (
                                        <p data-testid="note-popover-long-warning"
                                            className="mt-1.5 text-[11px] text-amber-700">
                                            Long {noteKind}s look messy in PDFs — consider trimming or splitting.
                                        </p>
                                    )}
                                    {/* Phase H.13b — Insert from library (footnote/citation clauses). */}
                                    <div className="mt-1.5 flex items-center justify-between">
                                        <button
                                            type="button"
                                            data-testid="note-popover-insert-from-library"
                                            className="text-[11px] text-blue-700 hover:text-blue-900 underline inline-flex items-center gap-1"
                                            onClick={() => { setClausePickerMode("footnote"); setClausePickerOpen(true); }}
                                        >
                                            <BookOpen size={11} /> Insert from library
                                        </button>
                                        <span className="text-[10px] text-slate-400">
                                            {noteDraft.length} chars
                                        </span>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between gap-2">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleDeleteNote}
                                            data-testid="note-popover-delete"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 size={12} className="mr-1" /> Delete
                                        </Button>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={handleCancelNote}
                                                data-testid="note-popover-cancel"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="button"
                                                size="sm"
                                                onClick={handleSaveNote}
                                                data-testid="note-popover-save"
                                                className="bg-blue-900 hover:bg-blue-800"
                                            >
                                                Save
                                            </Button>
                                        </div>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        )}
                        {tab === "html" && (
                            <textarea
                                data-testid="template-body-html-textarea"
                                value={htmlDraft}
                                onChange={(e) => setHtmlDraft(e.target.value)}
                                spellCheck={false}
                                className="w-full min-h-[480px] p-5 font-mono text-sm leading-relaxed outline-none resize-y bg-white"
                            />
                        )}
                        {tab === "preview" && (
                            <div
                                data-testid="template-body-preview"
                                className="min-h-[480px] p-6 bg-white prose prose-slate max-w-none">
                                {/* Phase H.12a — preview the running header/footer
                                    visually muted so authors can sanity-check
                                    them. PDF renders them on EVERY page; the
                                    in-app preview just shows them once. */}
                                {headerHtml && headerHtml.trim() && (
                                    <div data-testid="preview-header"
                                        className="not-prose mb-5 pb-3 border-b border-dashed border-slate-300 text-slate-500 text-sm"
                                        dangerouslySetInnerHTML={{ __html: headerHtml }} />
                                )}
                                <div data-testid="preview-body"
                                    dangerouslySetInnerHTML={{
                                        __html: currentSavedHtml ||
                                            "<p class='text-slate-400'>Nothing to preview yet.</p>",
                                    }} />
                                {footerHtml && footerHtml.trim() && (
                                    <div data-testid="preview-footer"
                                        className="not-prose mt-5 pt-3 border-t border-dashed border-slate-300 text-slate-500 text-sm text-center"
                                        dangerouslySetInnerHTML={{ __html: footerHtml }} />
                                )}
                            </div>
                        )}

                        {/* Footer status bar — Phase H.11: word-count detail popover trigger */}
                        <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between flex-wrap gap-2 text-xs"
                            data-testid="editor-status-bar">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button type="button"
                                        data-testid="editor-stats-trigger"
                                        title="Document statistics"
                                        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 hover:bg-white rounded px-2 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                        <BarChart3 size={12} />
                                        <span data-testid="editor-word-count">{wordCount} words</span>
                                        <span aria-hidden>·</span>
                                        <span data-testid="editor-char-count">{charCount} chars</span>
                                        <span aria-hidden>·</span>
                                        <span>{detected.length} tags</span>
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-3" data-testid="editor-stats-popover">
                                    <p className="text-xs font-medium tracking-wider uppercase text-slate-500 mb-2">
                                        Document statistics
                                    </p>
                                    <dl className="text-sm grid grid-cols-2 gap-y-1.5">
                                        <dt className="text-slate-500">Words</dt>
                                        <dd className="text-right text-slate-900 tabular-nums" data-testid="stats-words">{stats?.words ?? 0}</dd>
                                        <dt className="text-slate-500">Characters (w/ spaces)</dt>
                                        <dd className="text-right text-slate-900 tabular-nums" data-testid="stats-chars">{stats?.chars ?? 0}</dd>
                                        <dt className="text-slate-500">Characters (no spaces)</dt>
                                        <dd className="text-right text-slate-900 tabular-nums" data-testid="stats-chars-no-spaces">{stats?.charsNoSpaces ?? 0}</dd>
                                        <dt className="text-slate-500">Sentences</dt>
                                        <dd className="text-right text-slate-900 tabular-nums" data-testid="stats-sentences">{stats?.sentences ?? 0}</dd>
                                        <dt className="text-slate-500">Paragraphs</dt>
                                        <dd className="text-right text-slate-900 tabular-nums" data-testid="stats-paragraphs">{stats?.paragraphs ?? 0}</dd>
                                        <dt className="text-slate-500">Reading time</dt>
                                        <dd className="text-right text-slate-900" data-testid="stats-reading">~{stats?.readingMin ?? 0} min</dd>
                                        <dt className="text-slate-500">Merge tags</dt>
                                        <dd className="text-right text-slate-900 tabular-nums" data-testid="stats-tags">{stats?.tags ?? 0}</dd>
                                    </dl>
                                </PopoverContent>
                            </Popover>
                            <div data-testid="editor-tag-summary" className="text-slate-500 flex items-center gap-3">
                                {unknown.length > 0 && (
                                    <span data-testid="editor-unknown-tags" className="inline-flex items-center gap-1 text-red-700">
                                        <AlertTriangle size={12} /> {unknown.length} unknown: {unknown.join(", ")}
                                    </span>
                                )}
                                {/* Phase H.13c — variable validator toggle. */}
                                <button
                                    type="button"
                                    data-testid="variable-validator-toggle"
                                    aria-pressed={validatorOpen}
                                    title={validatorOpen ? "Hide variable validator" : "Show variable validator"}
                                    onClick={() => setValidatorOpen((v) => !v)}
                                    className={`inline-flex items-center gap-1.5 rounded px-2 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${validatorOpen
                                        ? "bg-blue-900 text-white hover:bg-blue-800"
                                        : "text-slate-600 hover:text-slate-900 hover:bg-white"
                                        }`}
                                >
                                    <ListChecks size={12} />
                                    Variables
                                </button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <div className="mt-4 flex items-center justify-end gap-3">
                    <AutoSavePill
                        state={autoState}
                        savedAt={savedAt}
                        nowTick={nowTick}
                        error={autoError}
                        enabled={Boolean(onAutoSave)}
                        retryAt={retryAt}
                        onRetry={() => {
                            // Phase H.11.1 — manual retry resets the attempt
                            // counter so the user gets a fresh 3-retry sequence
                            // on failure, and clears any pending auto-retry.
                            if (retryRef.current) { clearTimeout(retryRef.current); retryRef.current = null; }
                            setRetryAt(null);
                            retryCountRef.current = 0;
                            flushAutoSave();
                        }} />
                    <Button onClick={handleSubmit} disabled={saving}
                        data-testid="template-save-button"
                        className="bg-blue-900 hover:bg-blue-800">
                        {saving ? "Saving…" : submitLabel}
                    </Button>
                </div>
            </div>

            {/* Right rail: merge tags */}
            <aside className="lg:sticky lg:top-20 self-start" data-testid="merge-tags-rail">
                <Card>
                    <CardContent className="p-4">
                        <h3 className="font-display font-semibold text-slate-900 mb-2">Merge tags</h3>
                        <p className="text-xs text-slate-500 mb-3">
                            Click any tag to insert it at the cursor.
                        </p>
                        <div className="relative mb-2">
                            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input data-testid="merge-tags-search" placeholder="Search tags…"
                                value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
                                className="pl-8 h-9" />
                        </div>
                        {/* Phase MT-UI 2 — bulk expand / collapse. Subtle text
                            links, right-aligned. ``allCount`` is the total
                            distinct groups in the registry; ``expandedCount``
                            uses the PERSISTED manual set (not the search
                            override), so the disabled state reflects what
                            "Collapse all" / "Expand all" would actually do
                            when search is cleared. */}
                        {(() => {
                            const allCount = Object.keys(groupedAll).length;
                            const expandedCount = expandedGroups.size;
                            const everythingExpanded = allCount > 0 && expandedCount >= allCount;
                            const everythingCollapsed = expandedCount === 0;
                            // Hide entirely while the registry is still loading
                            // so we don't flash a useless control row.
                            if (allCount === 0) return null;
                            return (
                                <div className="flex items-center justify-end gap-3 mb-2 text-[11px] select-none">
                                    <button
                                        type="button"
                                        onClick={expandAllGroups}
                                        disabled={everythingExpanded}
                                        data-testid="merge-tags-expand-all"
                                        className="text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline transition-colors"
                                    >
                                        Expand all
                                    </button>
                                    <span className="text-slate-300" aria-hidden="true">·</span>
                                    <button
                                        type="button"
                                        onClick={collapseAllGroups}
                                        disabled={everythingCollapsed}
                                        data-testid="merge-tags-collapse-all"
                                        className="text-slate-500 hover:text-slate-700 hover:underline disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline transition-colors"
                                    >
                                        Collapse all
                                    </button>
                                </div>
                            );
                        })()}
                        <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                            {Object.keys(groupedAll).length === 0 && tagRegistry.length === 0 && (
                                <p className="text-sm text-slate-500">Loading tags…</p>
                            )}
                            {Object.entries(groupedAll).map(([g, allTags]) => {
                                // Phase MT-UI — when a search is active and this
                                // group has zero matches, hide the header entirely
                                // (matches the prior "no results" intuition where
                                // empty groups didn't appear). When search is
                                // empty, every group header always shows.
                                const visibleTags = grouped[g] || [];
                                if (tagFilter.trim() && visibleTags.length === 0) return null;
                                const expanded = isGroupExpanded(g);
                                return (
                                    <section
                                        key={g}
                                        className="border border-slate-200 rounded-md overflow-hidden"
                                        data-testid={`merge-tag-group-${g.replace(/\s+/g, "-")}`}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleGroup(g)}
                                            aria-expanded={expanded}
                                            data-testid={`merge-tag-group-toggle-${g.replace(/\s+/g, "-")}`}
                                            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <ChevronRight
                                                size={14}
                                                className={`text-slate-500 shrink-0 transition-transform duration-150 ${expanded ? "rotate-90" : ""}`}
                                            />
                                            <span className="text-xs font-medium tracking-wider uppercase text-slate-700 flex-1 truncate">
                                                {g}
                                            </span>
                                            <span className="text-[10px] font-medium text-slate-500 tabular-nums">
                                                ({allTags.length})
                                            </span>
                                        </button>
                                        {/* The grid-rows-[0fr↔1fr] trick gives a
                                            smooth pure-CSS animation to ``height:
                                            auto`` without a JS measure step. The
                                            inner div needs ``min-h-0`` so the
                                            grid track can collapse to zero. */}
                                        <div
                                            className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                                        >
                                            <div className="min-h-0 overflow-hidden">
                                                <div className="flex flex-wrap gap-1 p-2">
                                                    {visibleTags.map((t) => (
                                                        <button
                                                            type="button"
                                                            key={t.tag}
                                                            onClick={() => handleInsertTag(t.tag)}
                                                            data-testid={`merge-tag-${t.tag.replace(/\./g, "-")}`}
                                                            title={t.label}
                                                            className={`text-xs px-2 py-1 rounded-full border transition-colors ${t.type === "signature"
                                                                ? "bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100"
                                                                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"
                                                                }`}
                                                        >
                                                            {`{{${t.tag}}}`}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                );
                            })}
                            {/* Active-search no-results state. */}
                            {tagFilter.trim() && Object.keys(grouped).length === 0 && (
                                <p className="text-sm text-slate-500" data-testid="merge-tags-no-results">
                                    No tags match.
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </aside>

            <FindReplaceDialog editor={ed} open={findOpen} onOpenChange={setFindOpen} />

            {/* Phase H.13a — Version history drawer. Mounted as a sibling so
                the fixed-position backdrop & aside cover the full viewport. */}
            <VersionHistoryDrawer
                open={versionDrawerOpen}
                onClose={() => setVersionDrawerOpen(false)}
                templateId={templateId}
                canRestore={canRestoreVersions}
                currentBodyHtml={persistedBodyHtml}
                onRestored={(updated) => {
                    if (onVersionRestored) onVersionRestored(updated);
                }}
            />

            {/* Phase H.13b — Clause picker modal. Two-mode insert:
                  - "body" → drops clause.body_html at the editor's cursor
                    position as a one-time copy.
                  - "footnote" → replaces the current footnote/endnote
                    popover draft with the clause's plain-text version. */}
            <ClausePickerModal
                open={clausePickerOpen}
                onOpenChange={setClausePickerOpen}
                mode={clausePickerMode}
                onInsert={(clause) => {
                    if (clausePickerMode === "footnote") {
                        setNoteDraft(clause._plain_text || "");
                    } else if (ed) {
                        ed.chain().focus().insertContent(clause.body_html || "").run();
                    }
                }}
            />

            {/* Phase H.13c — Variable validator side panel. Re-derives
                its merge-tag list from the current body+header+footer
                HTML each time it's open; nothing persists across
                close/reopen. Sibling to VersionHistoryDrawer so both
                toggles are independent. */}
            <VariableValidatorPanel
                open={validatorOpen}
                onClose={() => setValidatorOpen(false)}
                bodyHtml={currentSavedHtml}
                headerHtml={headerHtml}
                footerHtml={footerHtml}
                tagRegistry={tagRegistry}
                editorWrapRef={editorWrapRef}
            />
        </div>
    );
});

export default TemplateEditor;

// ------------------------------ Sub-components ------------------------------

function LinkPopover({ editor, onDone }) {
    const existing = editor?.getAttributes("link") || {};
    const [href, setHref] = useState(existing.href || "");
    const [newTab, setNewTab] = useState(existing.target === "_blank");
    const [linkText, setLinkText] = useState("");
    const hasSelection =
        editor && editor.state.selection.from !== editor.state.selection.to;

    const apply = () => {
        if (!editor) return onDone();
        if (!/^https?:\/\//i.test(href)) {
            toast.error("Link must start with http:// or https://");
            return;
        }
        const attrs = { href, target: newTab ? "_blank" : null };
        if (!hasSelection && linkText.trim()) {
            editor.chain().focus()
                .insertContent(`<a href="${href}"${newTab ? ' target="_blank"' : ""}>${linkText.trim()}</a>`)
                .run();
        } else {
            editor.chain().focus().extendMarkRange("link").setLink(attrs).run();
        }
        onDone();
    };
    const clear = () => {
        editor?.chain().focus().unsetLink().run();
        onDone();
    };

    return (
        <div className="space-y-2">
            <Label className="text-xs">URL</Label>
            <Input value={href} onChange={(e) => setHref(e.target.value)}
                placeholder="https://example.com" data-testid="link-url-input" autoFocus />
            {!hasSelection && (
                <>
                    <Label className="text-xs">Link text</Label>
                    <Input value={linkText} onChange={(e) => setLinkText(e.target.value)}
                        placeholder="Click here" data-testid="link-text-input" />
                </>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={newTab}
                    onChange={(e) => setNewTab(e.target.checked)}
                    data-testid="link-newtab-checkbox" />
                Open in new tab
            </label>
            <div className="flex justify-between pt-1">
                <Button variant="outline" size="sm" onClick={clear} data-testid="link-clear">Remove</Button>
                <Button size="sm" className="bg-blue-900 hover:bg-blue-800"
                    onClick={apply} data-testid="link-apply">Apply</Button>
            </div>
        </div>
    );
}

function ImagePopover({ editor, onDone }) {
    const [src, setSrc] = useState("");
    const [alt, setAlt] = useState("");
    const apply = () => {
        if (!editor || !/^https?:\/\//i.test(src)) {
            toast.error("Image URL must start with http:// or https://");
            return;
        }
        editor.chain().focus().setImage({ src, alt }).run();
        onDone();
    };
    return (
        <div className="space-y-2">
            <Label className="text-xs">Image URL</Label>
            <Input value={src} onChange={(e) => setSrc(e.target.value)}
                placeholder="https://…" data-testid="image-url-input" autoFocus />
            <Label className="text-xs">Alt text</Label>
            <Input value={alt} onChange={(e) => setAlt(e.target.value)}
                placeholder="Describe the image" data-testid="image-alt-input" />
            <div className="flex justify-end pt-1">
                <Button size="sm" className="bg-blue-900 hover:bg-blue-800"
                    onClick={apply} data-testid="image-apply">Insert</Button>
            </div>
        </div>
    );
}

function TablePicker({ editor, onDone }) {
    const [rows, setRows] = useState(3);
    const [cols, setCols] = useState(3);
    const clamp = (n) => Math.min(10, Math.max(1, parseInt(n || "1", 10) || 1));
    const apply = () => {
        editor?.chain().focus()
            .insertTable({ rows: clamp(rows), cols: clamp(cols), withHeaderRow: true })
            .run();
        onDone();
    };
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label className="text-xs">Rows</Label>
                    <Input type="number" min={1} max={10} value={rows}
                        onChange={(e) => setRows(clamp(e.target.value))}
                        data-testid="table-rows-input" />
                </div>
                <div>
                    <Label className="text-xs">Columns</Label>
                    <Input type="number" min={1} max={10} value={cols}
                        onChange={(e) => setCols(clamp(e.target.value))}
                        data-testid="table-cols-input" />
                </div>
            </div>
            <p className="text-[11px] text-slate-500">Max 10×10. Header row included.</p>
            <div className="flex justify-end">
                <Button size="sm" className="bg-blue-900 hover:bg-blue-800"
                    onClick={apply} data-testid="table-apply">Insert</Button>
            </div>
        </div>
    );
}

// Find & replace modal. Operates on document text nodes only via TipTap
// transaction replacements — never touches inline marks/nodes (so merge tag
// chips, links, images survive intact).
function FindReplaceDialog({ editor, open, onOpenChange }) {
    const [find, setFind] = useState("");
    const [repl, setRepl] = useState("");
    const [insensitive, setInsensitive] = useState(true);
    const [cursor, setCursor] = useState(0);

    const search = (fromPos = 0) => {
        if (!editor || !find) return null;
        const text = editor.state.doc.textBetween(0, editor.state.doc.content.size, "\n", " ");
        const needle = insensitive ? find.toLowerCase() : find;
        const hay = insensitive ? text.toLowerCase() : text;
        const idx = hay.indexOf(needle, fromPos);
        if (idx < 0) return null;
        return { from: idx, to: idx + find.length };
    };

    const replaceNext = () => {
        if (!editor) return;
        const m = search(cursor);
        if (!m) { toast("No more matches"); setCursor(0); return; }
        // Map text-offset → doc-position by walking nodes.
        const docPositions = textOffsetsToDocPositions(editor, m.from, m.to);
        if (!docPositions) return;
        editor.chain().focus()
            .insertContentAt({ from: docPositions.from, to: docPositions.to }, repl, {
                updateSelection: false,
            }).run();
        setCursor(m.from + repl.length);
    };

    const replaceAll = () => {
        if (!editor || !find) return;
        let n = 0;
        // Iterate from end of doc to start so positions stay stable.
        const text = editor.state.doc.textBetween(0, editor.state.doc.content.size, "\n", " ");
        const needle = insensitive ? find.toLowerCase() : find;
        const hay = insensitive ? text.toLowerCase() : text;
        const matches = [];
        let i = 0;
        while (i <= hay.length) {
            const j = hay.indexOf(needle, i);
            if (j < 0) break;
            matches.push({ from: j, to: j + find.length });
            i = j + find.length;
            if (find.length === 0) break;
        }
        for (let k = matches.length - 1; k >= 0; k--) {
            const docPositions = textOffsetsToDocPositions(editor, matches[k].from, matches[k].to);
            if (!docPositions) continue;
            editor.chain()
                .insertContentAt({ from: docPositions.from, to: docPositions.to }, repl, {
                    updateSelection: false,
                }).run();
            n++;
        }
        toast.success(`${n} replacement${n === 1 ? "" : "s"}`);
        setCursor(0);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="find-replace-dialog">
                <DialogHeader>
                    <DialogTitle>Find &amp; Replace</DialogTitle>
                    <DialogDescription>
                        Operates on plain text. Merge tags, links, and images are preserved.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Find</Label>
                        <Input value={find} onChange={(e) => { setFind(e.target.value); setCursor(0); }}
                            data-testid="find-input" autoFocus />
                    </div>
                    <div>
                        <Label className="text-xs">Replace with</Label>
                        <Input value={repl} onChange={(e) => setRepl(e.target.value)}
                            data-testid="replace-input" />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                        <input type="checkbox" checked={insensitive}
                            onChange={(e) => setInsensitive(e.target.checked)}
                            data-testid="find-case-insensitive" />
                        Case-insensitive
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={replaceNext} data-testid="replace-next">Replace next</Button>
                    <Button className="bg-blue-900 hover:bg-blue-800"
                        onClick={replaceAll} data-testid="replace-all">Replace all</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Map an offset within the plain-text doc serialization to ProseMirror doc
// positions. Walk the doc, accumulating text node lengths and a single
// newline per block boundary (matching what textBetween used with "\n").
function textOffsetsToDocPositions(editor, fromOff, toOff) {
    if (!editor) return null;
    const doc = editor.state.doc;
    let offset = 0;
    let fromPos = null;
    let toPos = null;
    let lastBlock = null;
    doc.descendants((node, pos) => {
        if (node.isBlock) {
            if (lastBlock !== null) {
                // Block boundary contributes one virtual newline in the
                // textBetween("\n", " ") stream. Don't advance pos but count it.
                offset += 1;
            }
            lastBlock = pos;
            return true;
        }
        if (node.isText) {
            const len = node.text.length;
            const start = offset;
            const end = offset + len;
            if (fromPos === null && fromOff >= start && fromOff <= end) {
                fromPos = pos + (fromOff - start);
            }
            if (toPos === null && toOff >= start && toOff <= end) {
                toPos = pos + (toOff - start);
            }
            offset = end;
        } else if (node.isInline && node.isAtom) {
            // MergeTagNode / image atoms — render as a single " " in
            // textBetween. Advance offset by 1, but do NOT split into here.
            offset += 1;
        }
        return true;
    });
    if (fromPos === null || toPos === null) return null;
    return { from: fromPos, to: toPos };
}

// ------------------------------ Phase H.11 helpers ------------------------------

function AutoSavePill({ state, savedAt, nowTick, error, enabled, retryAt, onRetry }) {
    if (!enabled) return null;
    const ago = savedAt ? Math.max(0, Math.round((Date.now() - savedAt) / 1000)) : 0;
    // Phase H.11.1 — seconds until next auto-retry fires; null if no pending retry.
    const retryIn = retryAt
        ? Math.max(0, Math.ceil((retryAt - Date.now()) / 1000))
        : null;
    const _ = nowTick; // re-render trigger
    let body, cls, testid;
    if (state === "saving") {
        body = (<><Loader2 size={12} className="animate-spin" /> Saving</>);
        cls = "bg-blue-50 text-blue-900 border-blue-200";
        testid = "autosave-state-saving";
    } else if (state === "dirty") {
        body = (<>Editing…</>);
        cls = "bg-slate-50 text-slate-600 border-slate-200";
        testid = "autosave-state-dirty";
    } else if (state === "blocked") {
        // Phase H.11.1 — required-field gate. No PATCH fires while in this state.
        body = (
            <>
                <AlertTriangle size={12} />
                Save blocked — fix required fields
                {error ? <span className="text-amber-800/80">· {String(error).slice(0, 60)}</span> : null}
            </>
        );
        cls = "bg-amber-50 text-amber-900 border-amber-300";
        testid = "autosave-state-blocked";
    } else if (state === "error") {
        // Phase H.11.1 — show countdown while an auto-retry is pending,
        // otherwise show the terminal "click to retry" manual button.
        body = retryIn !== null ? (
            <>
                <AlertTriangle size={12} />
                Save failed — retrying in {retryIn}s
                <button type="button" onClick={onRetry}
                    data-testid="autosave-retry-now"
                    className="ml-1 underline inline-flex items-center gap-0.5">
                    <RefreshCw size={11} /> Retry now
                </button>
            </>
        ) : (
            <>
                <AlertTriangle size={12} />
                Save failed — click to retry
                <button type="button" onClick={onRetry}
                    data-testid="autosave-retry"
                    className="ml-1 underline inline-flex items-center gap-0.5">
                    <RefreshCw size={11} /> Retry
                </button>
            </>
        );
        cls = "bg-red-50 text-red-700 border-red-200";
        testid = retryIn !== null ? "autosave-state-error-retrying" : "autosave-state-error-terminal";
    } else if (state === "saved") {
        const label = ago < 5
            ? "Saved just now"
            : `Saved ${ago}s ago`;
        body = (<><CheckCircle2 size={12} className="text-emerald-700" /> {label}</>);
        cls = "bg-emerald-50 text-emerald-900 border-emerald-200";
        testid = "autosave-state-saved";
    } else {
        body = (<><CheckCircle2 size={12} className="text-slate-500" /> Saved</>);
        cls = "bg-white text-slate-500 border-slate-200";
        testid = "autosave-state-idle";
    }
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${cls}`}
            aria-live="polite">
            {body}
        </span>
    );
}

// Phase H.11 — open the rendered HTML in a new window with print-friendly
// styling (1in margins, serif body, page break support) and auto-trigger
// the browser's print dialog. The user can cancel to just see the layout.
function openPrintPreview(html, name) {
    const w = window.open("", "_blank", "noopener");
    if (!w) {
        toast.error("Pop-up blocker prevented opening the print preview");
        return;
    }
    const title = (name || "Template").replace(/[<>&]/g, "");
    w.document.write(`<!doctype html>
<html><head><meta charset="utf-8" />
<title>Print preview — ${title}</title>
<style>
  @page { size: 8.5in 11in; margin: 1in; }
  html, body { font-family: 'Times New Roman', Times, serif; color: #0f172a;
    font-size: 12pt; line-height: 1.5; }
  body { margin: 0; padding: 1in; max-width: 8.5in; box-sizing: border-box; }
  h1 { font-size: 22pt; } h2 { font-size: 18pt; } h3 { font-size: 16pt; }
  h4 { font-size: 14pt; } h5, h6 { font-size: 12pt; }
  table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
  th, td { border: 1px solid #94a3b8; padding: 6pt 8pt; vertical-align: top; }
  th { background: #f1f5f9; }
  hr[style*="page-break-after"] { page-break-after: always; border: 0; height: 0; }
  hr { border: 0; border-top: 1px solid #e2e8f0; margin: 1em 0; }
  .signature-line { margin: 1.2em 0; }
  .signature-line .sig-rule { border-bottom: 1px solid #0f172a; height: 1.6em; width: 60%; }
  .signature-line .sig-label { font-size: 10pt; color: #475569; margin-top: 2pt; }
  @media screen { body { background: #fff; box-shadow: 0 0 12px rgba(15,23,42,0.08);
    margin: 1in auto; } }
</style></head>
<body>${html || ""}</body>
<script>window.addEventListener("load", function() { setTimeout(function(){ window.print(); }, 200); });</script>
</html>`);
    w.document.close();
}
